/**
 * The report's filter bar, and the pure helpers that turn what it shows into
 * issueMetrics' arguments. Days are the admin's own, in the browser's time
 * zone: "today" starts at their midnight, not at midnight UTC (the evening
 * before, for an admin in the US). ant takes the range as two instants.
 */

import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  IssueMetricsVariables,
  IssuePartnerOption,
  IssueSource,
  IssueSystemOption,
  systemsOfPartner,
} from './useIssueMetrics';

export type RangePreset = 'this_week' | 'last_7' | 'last_30' | 'this_month' | 'custom';

export const RANGE_PRESETS: Array<{ id: Exclude<RangePreset, 'custom'>; label: string }> =
  [
    { id: 'this_week', label: 'This week' },
    { id: 'last_7', label: 'Last 7 days' },
    { id: 'last_30', label: 'Last 30 days' },
    { id: 'this_month', label: 'This month' },
  ];

/** ant's product families, as issueMetrics' hardwareFamily names them. */
export const HARDWARE_FAMILIES: Array<{ id: string; label: string }> = [
  { id: 'bike', label: 'Bikes' },
  { id: 'scooter', label: 'Scooters' },
  { id: 'kayak', label: 'Kayaks' },
  { id: 'life_jacket', label: 'Life jackets' },
  { id: 'lawn_game', label: 'Lawn games' },
  { id: 'locker', label: 'Lockers' },
  { id: 'other', label: 'Other' },
];

export const SOURCES: Array<{ id: IssueSource; label: string }> = [
  { id: 'CS_CHAT', label: 'CS chat' },
  { id: 'RIDER_APP', label: 'Rider app' },
];

export interface IssueFilterState {
  preset: RangePreset;
  /** First local day of the range, "2026-09-22". */
  fromDay: string;
  /** Last local day of the range, included. */
  toDay: string;
  partnerId: number | null;
  systemId: string | null;
  hardwareFamily: string | null;
  /** Never empty: the last source cannot be switched off. */
  sources: IssueSource[];
  countsAsNew: boolean;
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** "2026-09-22" for the local day `date` falls in. */
export const localDayKey = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * Local midnight of the day `days` after the one `date` falls in (negative:
 * before). Counted in calendar days, not in 24 h steps, so a day that is 23
 * or 25 hours long (the clocks change) moves nothing.
 */
const localMidnight = (date: Date, days = 0): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** Local midnight of a "2026-09-22" day. */
const dayStart = (day: string): Date => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** The local days a preset covers, today included. */
export function presetRange(
  preset: Exclude<RangePreset, 'custom'>,
  now: Date = new Date()
): { fromDay: string; toDay: string } {
  const today = localMidnight(now);
  const toDay = localDayKey(today);
  switch (preset) {
    case 'this_week': {
      // Monday first: getDay is 0 on Sunday.
      const sinceMonday = (today.getDay() + 6) % 7;
      return { fromDay: localDayKey(localMidnight(today, -sinceMonday)), toDay };
    }
    case 'last_7':
      return { fromDay: localDayKey(localMidnight(today, -6)), toDay };
    case 'last_30':
      return { fromDay: localDayKey(localMidnight(today, -29)), toDay };
    case 'this_month':
      return {
        fromDay: localDayKey(new Date(today.getFullYear(), today.getMonth(), 1)),
        toDay,
      };
    default:
      return { fromDay: toDay, toDay };
  }
}

/** What the page opens on: the last 7 days, every source, duplicates counted. */
export function defaultFilters(now: Date = new Date()): IssueFilterState {
  return {
    preset: 'last_7',
    ...presetRange('last_7', now),
    partnerId: null,
    systemId: null,
    hardwareFamily: null,
    sources: SOURCES.map((s) => s.id),
    countsAsNew: true,
  };
}

/**
 * issueMetrics' arguments for the filters: [from, to) from the first day's
 * local midnight to the local midnight after the last day (capped at now, so
 * the range never reaches into the future), both as ISO instants, every
 * source as null, and the rest as is.
 */
export function toIssueVariables(
  filters: IssueFilterState,
  now: Date = new Date()
): IssueMetricsVariables {
  const from = dayStart(filters.fromDay).getTime();
  const end = localMidnight(dayStart(filters.toDay), 1).getTime();
  const to = Math.min(end, now.getTime());
  const everySource = SOURCES.every((s) => filters.sources.includes(s.id));
  return {
    from: new Date(from).toISOString(),
    to: new Date(Math.max(to, from)).toISOString(),
    partnerId: filters.partnerId,
    systemId: filters.systemId,
    hardwareFamily: filters.hardwareFamily,
    sources: everySource ? null : filters.sources,
    countsAsNew: filters.countsAsNew,
  };
}

const isDay = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

interface IssueFiltersProps {
  filters: IssueFilterState;
  onChange: (filters: IssueFilterState) => void;
  partners: IssuePartnerOption[];
  systems: IssueSystemOption[];
  loading: boolean;
  onReload: () => void;
  downloading: boolean;
  onDownload: () => void;
  /** The clock the presets count back from; tests pin it. */
  now?: () => Date;
}

export default function IssueFilters({
  filters,
  onChange,
  partners,
  systems,
  loading,
  onReload,
  downloading,
  onDownload,
  now = () => new Date(),
}: IssueFiltersProps) {
  const update = (patch: Partial<IssueFilterState>) => onChange({ ...filters, ...patch });
  const visibleSystems = systemsOfPartner(systems, filters.partnerId);

  const pickPreset = (preset: Exclude<RangePreset, 'custom'> | null) => {
    if (!preset) return;
    update({ preset, ...presetRange(preset, now()) });
  };

  const pickDay = (key: 'fromDay' | 'toDay', value: string) => {
    if (!isDay(value)) return;
    const next = { ...filters, preset: 'custom' as const, [key]: value };
    if (next.fromDay > next.toDay) {
      if (key === 'fromDay') next.toDay = value;
      else next.fromDay = value;
    }
    onChange(next);
  };

  const pickPartner = (value: string) => {
    const partnerId = value === '' ? null : Number(value);
    const keepSystem =
      filters.systemId !== null &&
      systemsOfPartner(systems, partnerId).some((s) => s.id === filters.systemId);
    update({ partnerId, systemId: keepSystem ? filters.systemId : null });
  };

  const pickSources = (value: IssueSource[]) => {
    // The last source stays on: a report of nothing has no use.
    if (value.length === 0) return;
    update({ sources: SOURCES.map((s) => s.id).filter((id) => value.includes(id)) });
  };

  return (
    <Stack spacing={1.5} data-testid="issue-filters">
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
        <ToggleButtonGroup
          value={filters.preset === 'custom' ? null : filters.preset}
          exclusive
          size="small"
          onChange={(_, v) => pickPreset(v)}
          aria-label="Range"
        >
          {RANGE_PRESETS.map((preset) => (
            <ToggleButton
              key={preset.id}
              value={preset.id}
              sx={{ textTransform: 'none', px: 1.5 }}
            >
              {preset.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          type="date"
          size="small"
          label="From"
          value={filters.fromDay}
          onChange={(e) => pickDay('fromDay', e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'issues-from', max: filters.toDay }}
          sx={{ width: 170 }}
        />
        <TextField
          type="date"
          size="small"
          label="To"
          value={filters.toDay}
          onChange={(e) => pickDay('toDay', e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'issues-to', min: filters.fromDay }}
          sx={{ width: 170 }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={onReload} disabled={loading} aria-label="Refresh report">
              {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            downloading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <FileDownloadIcon />
            )
          }
          onClick={onDownload}
          disabled={downloading}
        >
          Download CSV
        </Button>
      </Stack>
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
        <TextField
          select
          size="small"
          label="Partner"
          value={filters.partnerId === null ? '' : String(filters.partnerId)}
          onChange={(e) => pickPartner(e.target.value)}
          sx={{ minWidth: 200 }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          inputProps={{ 'data-testid': 'issues-partner' }}
        >
          <MenuItem value="">All partners</MenuItem>
          {partners.map((p) => (
            <MenuItem key={p.id} value={String(p.id)}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="System"
          value={filters.systemId ?? ''}
          onChange={(e) => update({ systemId: e.target.value || null })}
          sx={{ minWidth: 220 }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          inputProps={{ 'data-testid': 'issues-system' }}
        >
          <MenuItem value="">All systems</MenuItem>
          {visibleSystems.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name || 'Unnamed system'}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Hardware"
          value={filters.hardwareFamily ?? ''}
          onChange={(e) => update({ hardwareFamily: e.target.value || null })}
          sx={{ minWidth: 160 }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          inputProps={{ 'data-testid': 'issues-hardware' }}
        >
          <MenuItem value="">All hardware</MenuItem>
          {HARDWARE_FAMILIES.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          value={filters.sources}
          size="small"
          onChange={(_, v: IssueSource[]) => pickSources(v)}
          aria-label="Sources"
        >
          {SOURCES.map((source) => (
            <ToggleButton
              key={source.id}
              value={source.id}
              sx={{ textTransform: 'none', px: 1.5 }}
            >
              {source.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={filters.countsAsNew}
              onChange={(e) => update({ countsAsNew: e.target.checked })}
              inputProps={{ 'aria-label': 'Continuing and duplicate count as new' }}
            />
          }
          label="Continuing and duplicate count as new"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
        />
      </Stack>
    </Stack>
  );
}
